/**
 * element-selection.js — sibling multi-selection for repeat-root elements
 * (Polish 16C1), and the single write path into its store.
 *
 * Mirrors the section-level split deliberately: `SelectionStore` (core/) holds
 * the Set with anchor/lead semantics and no policy; everything that decides
 * whether a click BECOMES a selection lives here. What differs is the identity
 * model, which is the whole point of 16C1 being separate from 16C2:
 *
 *   The unit is the REPEAT ROOT — the element `closest(itemSelector)` returns —
 *   and nothing else. Not the clicked descendant, not a styling target inside
 *   it. Selection ids are `${instanceId}\u001f${unitEid}`, where `unitEid` is
 *   `siblingUnitsNearEid`'s read-only identity eid for the unit.
 *
 * Scope, enforced by `_sameGroup`: same parent, same adapter kind, one section.
 * A modifier gesture that lands outside the current group COLLAPSES to the new
 * unit rather than spanning two groups — which is also why "what does delete
 * mean for a nav link plus a social link" never has to be answered.
 *
 * Gestures reach here (unlike section-level canvas multi-select, which was
 * rejected because a canvas click resolves to an editable first): the user
 * clicks directly on the card or icon, and the element tier is exactly what
 * resolves. `iframe:element-clicked` forwards `metaKey`/`shiftKey` from the
 * iframe, gated there on not-editing so Shift-click inside a live
 * contenteditable still extends the text selection.
 */

import { eventBus } from '../core/events.js';
import { SelectionStore } from '../core/selection-store.js';
import { resolveClickIntent } from '../ui/section-list-interaction.js';
import { bridge } from './bridge.js';
import { bulkDeleteElements, bulkDuplicateElements } from './element-actions.js';
import { siblingUnitsNearEid } from './element-insert.js';
import { getAdapterByKind } from './pattern-adapters.js';
import { resolveAdapterKindForPayload } from './adapters/freeform-block-adapter.js';
import { resolveSectionByInstance } from './section-utils.js';
import { isPickModeActive } from './ai-context-picker.js';
import { isPaintModeActive } from './format-painter.js';
import { isDomTarget } from '../shared/target-ref.js';

/** The element-selection singleton. Section selection has its own store. */
export const elementSelection = new SelectionStore();

/** Unit separator — an eid can never contain it, so the key parses back cleanly. */
const KEY_SEP = '\u001f';

/**
 * The group the current selection lives in. Reset whenever the selection
 * collapses into a different group; null whenever the selection is empty.
 * @type {{instanceId: string, adapterKind: string|null, itemSelector: string}|null}
 */
let _context = null;
let _installed = false;
/** The section HTML the selection was last pruned against (see _onStateChanged). */
let _lastPrunedHtml = null;

export function elementUnitKey(instanceId, unitEid) {
  return `${instanceId}${KEY_SEP}${unitEid}`;
}

export function parseElementUnitKey(key) {
  const i = typeof key === 'string' ? key.indexOf(KEY_SEP) : -1;
  if (i === -1) return null;
  return { instanceId: key.slice(0, i), eid: key.slice(i + 1) };
}

// ── reads ────────────────────────────────────────────────────────────────────

export function elementSelectionSize() {
  return elementSelection.size();
}

export function isElementSelectionMulti() {
  return elementSelection.isMulti();
}

/** The current group, or null when nothing is selected. */
export function getElementSelectionContext() {
  return _context ? { ..._context } : null;
}

/** Selected units in document order. @returns {{instanceId: string, eid: string}[]} */
export function getSelectedElementUnits() {
  return elementSelection.getIds().map(parseElementUnitKey).filter(Boolean);
}

/** The selected units' eids — the argument shape the bulk transforms take. */
export function getSelectedElementUnitEids() {
  return getSelectedElementUnits().map((u) => u.eid);
}

/**
 * Is the unit this payload resolves to part of the current selection?
 *
 * The gate that stops a bulk action firing on something the user is not
 * pointing at: right-clicking a unit OUTSIDE the selection must collapse to it
 * and act singly, exactly as every file manager does.
 */
export function isElementUnitSelected(payload, adapterKind) {
  const resolved = resolveElementUnit(payload, adapterKind);
  if (!resolved) return false;
  return elementSelection.isSelected(elementUnitKey(resolved.instanceId, resolved.unitEid));
}

// ── resolution ───────────────────────────────────────────────────────────────

/** The itemSelector for a kind, or null when the kind declares no repeat root. */
function itemSelectorFor(adapterKind) {
  return getAdapterByKind(adapterKind)?.itemSelector || null;
}

/**
 * Resolve a click payload to its repeat-root unit and sibling group.
 * Returns null when the payload is not part of a repeat pattern — a lone
 * heading has no siblings to multi-select, and must clear rather than pretend.
 *
 * @param {{instanceId?: string, eid?: string, ancestorChain?: object[]}} payload
 * @param {string|null} [adapterKind] — resolved from the chain when omitted
 * @returns {{instanceId, adapterKind, itemSelector, unitEid, index,
 *   units: string[], keys: string[]}|null}
 */
export function resolveElementUnit(payload, adapterKind) {
  if (!payload?.instanceId || !payload?.eid) return null;
  const kind =
    adapterKind !== undefined && adapterKind !== null
      ? adapterKind
      : resolveAdapterKindForPayload(payload);
  const itemSelector = itemSelectorFor(kind);
  if (!itemSelector) return null;

  const html = resolveSectionByInstance(payload.instanceId)?.section?.html;
  if (!html) return null;

  const group = siblingUnitsNearEid(html, payload.eid, { itemSelector });
  if (!group) return null;

  return {
    instanceId: payload.instanceId,
    adapterKind: kind,
    itemSelector,
    unitEid: group.unitEid,
    index: group.index,
    units: group.units,
    keys: group.units.map((eid) => elementUnitKey(payload.instanceId, eid))
  };
}

/** Same section, same adapter kind, same parent group — the 16C1 scope rule. */
function _sameGroup(resolved) {
  if (!_context || elementSelection.isEmpty()) return false;
  if (_context.instanceId !== resolved.instanceId) return false;
  if (_context.adapterKind !== resolved.adapterKind) return false;
  const lead = elementSelection.getLead();
  // Membership by enumerated siblings rather than by a parent id: eids are
  // stamped on editables only, so a repeat root's parent frequently has none
  // and a `${instanceId}\u001f${parentEid}` key would be null for both an
  // unrelated group and this one.
  return !!lead && resolved.keys.includes(lead);
}

// ── writes ───────────────────────────────────────────────────────────────────

/** Replace the selection with one unit, re-homing the group context. */
function _selectUnit(resolved, source) {
  _context = {
    instanceId: resolved.instanceId,
    adapterKind: resolved.adapterKind,
    itemSelector: resolved.itemSelector
  };
  elementSelection.select(elementUnitKey(resolved.instanceId, resolved.unitEid), { source });
}

/** Empty the selection and forget its group. */
export function clearElementSelection(reason) {
  _context = null;
  _lastPrunedHtml = null;
  elementSelection.clear({ source: 'element-selection', reason });
}

/**
 * Collapse a multi-selection to its lead. The 16A1 rule applied at element
 * level: any single-target action reached while a set is selected (Cmd+C, Cut,
 * Paste, Copy style, a rename, a drawer) first collapses, then runs unchanged —
 * so no entry point acquires bulk semantics by accident.
 * @returns {boolean} whether anything collapsed
 */
export function collapseElementSelection(reason) {
  if (!elementSelection.isMulti()) return false;
  const lead = elementSelection.getLead();
  if (!lead) {
    clearElementSelection(reason);
    return true;
  }
  elementSelection.select(lead, { source: 'element-selection', reason: reason || 'collapse' });
  return true;
}

/**
 * "Select all N in this group" — the discoverable path to a sibling
 * multi-selection, since modifier clicks are not discoverable on their own.
 * @returns {number} how many units ended up selected (0 when unresolvable)
 */
export function selectSiblingGroup(payload, adapterKind) {
  const resolved = resolveElementUnit(payload, adapterKind);
  if (!resolved || resolved.keys.length === 0) return 0;
  _context = {
    instanceId: resolved.instanceId,
    adapterKind: resolved.adapterKind,
    itemSelector: resolved.itemSelector
  };
  elementSelection.set(resolved.keys, {
    order: resolved.keys,
    anchor: resolved.keys[0],
    lead: elementUnitKey(resolved.instanceId, resolved.unitEid),
    source: 'element-selection:select-group'
  });
  return elementSelection.size();
}

/**
 * Select an explicit unit list inside one group — how a bulk duplicate hands
 * the selection to its clones as ONE write rather than N events.
 */
export function setElementSelection(instanceId, unitEids, { adapterKind, itemSelector } = {}) {
  const eids = (Array.isArray(unitEids) ? unitEids : []).filter(Boolean);
  if (!instanceId || eids.length === 0) return false;
  const selector = itemSelector || itemSelectorFor(adapterKind);
  if (!selector) return false;
  // Re-resolve through the group so the stored ids are unit identities, not
  // whatever eid inside the unit the caller happened to hold.
  const resolved = resolveElementUnit({ instanceId, eid: eids[0] }, adapterKind ?? null);
  if (!resolved) return false;
  const wanted = new Set();
  for (const eid of eids) {
    const each = resolveElementUnit({ instanceId, eid }, adapterKind ?? null);
    if (each) wanted.add(elementUnitKey(instanceId, each.unitEid));
  }
  if (wanted.size === 0) return false;
  _context = {
    instanceId,
    adapterKind: resolved.adapterKind,
    itemSelector: resolved.itemSelector
  };
  const ordered = resolved.keys.filter((key) => wanted.has(key));
  elementSelection.set(ordered.length ? ordered : Array.from(wanted), {
    order: resolved.keys,
    source: 'element-selection:set'
  });
  return true;
}

// ── bulk operations over the current selection ───────────────────────────────

/**
 * Delete every selected unit as ONE history entry. Returns false (and writes
 * nothing) when the selection is not a multi-selection — the single-unit path
 * keeps its own confirm-free delete + undo toast, unchanged.
 */
export function bulkDeleteSelectedElements() {
  if (!_context || !elementSelection.isMulti()) return false;
  const { ok } = bulkDeleteElements({
    instanceId: _context.instanceId,
    unitEids: getSelectedElementUnitEids(),
    itemSelector: _context.itemSelector
  });
  return ok;
}

/**
 * Duplicate every selected unit as ONE history entry, then move the selection
 * to the clones in a SINGLE write (never N events) — the element-level mirror
 * of 16A2b's bulk duplicate.
 */
export function bulkDuplicateSelectedElements() {
  if (!_context || !elementSelection.isMulti()) return false;
  const context = { ..._context };
  const { ok, newUnitEids } = bulkDuplicateElements({
    instanceId: context.instanceId,
    unitEids: getSelectedElementUnitEids(),
    itemSelector: context.itemSelector
  });
  if (!ok) return false;
  if (newUnitEids.length) {
    setElementSelection(context.instanceId, newUnitEids, {
      adapterKind: context.adapterKind,
      itemSelector: context.itemSelector
    });
  }
  return true;
}

// ── policy ───────────────────────────────────────────────────────────────────

function _onElementClicked(data) {
  if (!isDomTarget(data)) {
    if (!elementSelection.isEmpty()) clearElementSelection('non-dom-target');
    return;
  }
  // Pick mode consumes the click entirely (the element is tagged as agent
  // context, never selected) — same rule the section controller applies.
  if (isPickModeActive() && data?.instanceId && data?.eid) return;

  const resolved = resolveElementUnit(data, undefined);
  if (!resolved) {
    // A non-repeat editable is not a selectable unit. Clearing rather than
    // leaving the previous set standing is the element-tier analogue of the
    // 16A0.6 desync fix: destructive shortcuts must never point at something
    // the user is no longer looking at.
    if (!elementSelection.isEmpty()) clearElementSelection('non-repeat-element');
    return;
  }

  // Paint mode reads every editable click as "paint that target", so modifiers
  // are not selection gestures while it is armed.
  const intent = isPaintModeActive() ? 'replace' : resolveClickIntent(data);
  const key = elementUnitKey(resolved.instanceId, resolved.unitEid);

  if (intent === 'replace' || !_sameGroup(resolved)) {
    _selectUnit(resolved, 'element-clicked');
    return;
  }

  const source = `element-clicked:${intent}`;
  if (intent === 'toggle') {
    elementSelection.toggle(key, { order: resolved.keys, source });
    if (elementSelection.isEmpty()) _context = null;
    return;
  }
  elementSelection.extendTo(key, {
    order: resolved.keys,
    additive: intent === 'range-add',
    source
  });
}

/**
 * Content churn — the section's HTML changed under the selection (a bulk op,
 * an undo, a collab peer). Re-derive the group from committed state and drop
 * units that no longer exist.
 */
function _onStateChanged() {
  if (elementSelection.isEmpty() || !_context) return;
  // Units are derived from the section HTML, so an unchanged HTML cannot have
  // changed them. Without this guard every property write re-parses the whole
  // section — `state-changed` fires on EVERY update, including inside a
  // transaction (16B1), so a 40-frame scrub cost 40 DOMParse calls while an
  // element selection was live. Measured by element-restyle-tests.
  const currentHtml = resolveSectionByInstance(_context.instanceId)?.section?.html;
  if (currentHtml && currentHtml === _lastPrunedHtml) return;
  _lastPrunedHtml = currentHtml || null;

  const units = getSelectedElementUnits();
  const lead = parseElementUnitKey(elementSelection.getLead());
  const probeEid = lead?.eid || units[0]?.eid;
  const resolved = probeEid
    ? resolveElementUnit(
        { instanceId: _context.instanceId, eid: probeEid },
        _context.adapterKind ?? null
      )
    : null;
  if (!resolved) {
    // The lead itself is gone: fall back to validating each member on its own
    // so a delete of only the lead does not clear the whole set.
    const surviving = units
      .filter((u) => resolveElementUnit(u, _context.adapterKind ?? null))
      .map((u) => elementUnitKey(u.instanceId, u.eid));
    if (surviving.length === 0) clearElementSelection('pruned');
    else elementSelection.prune(new Set(surviving), { source: 'state-changed' });
    _restampBridge();
    return;
  }
  elementSelection.prune(new Set(resolved.keys), { source: 'state-changed' });
  if (elementSelection.isEmpty()) _context = null;
  // A surgical section resync replaces the wrapper's innerHTML, dropping the
  // member attrs. `prune` only notifies when membership actually changed, so
  // an unchanged selection would otherwise go invisible until the next click.
  _restampBridge();
}

// ── projection ───────────────────────────────────────────────────────────────

/**
 * Stamp the co-selected units in the canvas. The lead keeps its existing
 * `data-pb-selected-target` treatment (so `selected-editable-lifecycle` and the
 * glass overlay see no change at size 1); members get `data-pb-element-multi`.
 * An empty list clears the attrs — a stale member outline must not outlive the
 * selection that produced it.
 */
function _syncBridge(change) {
  const units = change.ids.map(parseElementUnitKey).filter(Boolean);
  const lead = parseElementUnitKey(change.lead);
  const multi = units.length > 1;
  bridge.send('set-multi-selected-elements', {
    instanceId: _context?.instanceId || lead?.instanceId || null,
    // The iframe climbs each eid to `closest(itemSelector)` before painting —
    // a unit identity eid can sit on a descendant of the repeat root.
    itemSelector: multi ? _context?.itemSelector || null : null,
    eids: multi ? units.map((u) => u.eid) : [],
    leadEid: multi ? lead?.eid || null : null
  });
}

/** Re-stamp the canvas without changing the selection (after a DOM rebuild). */
function _restampBridge() {
  if (!elementSelection.isMulti()) return;
  _syncBridge({ ids: elementSelection.getIds(), lead: elementSelection.getLead() });
}

// ── lifecycle ────────────────────────────────────────────────────────────────

/**
 * Install the controller — the sole writer of the element selection store.
 * @returns {() => void} uninstall
 */
export function initElementSelection() {
  if (_installed) return () => {};
  _installed = true;

  const offStore = elementSelection.subscribe((change) => {
    _syncBridge(change);
    eventBus.emit('element-selection:changed', {
      ...change,
      units: change.ids.map(parseElementUnitKey).filter(Boolean),
      context: getElementSelectionContext()
    });
  });

  const offs = [
    eventBus.on('iframe:element-clicked', _onElementClicked),
    eventBus.on('iframe:selected-editable-cleared', () =>
      clearElementSelection('selected-editable-cleared')
    ),
    eventBus.on('iframe:canvas-clicked', () => clearElementSelection('canvas-clicked')),
    eventBus.on('section-deselected', () => clearElementSelection('section-deselected')),
    eventBus.on('state-changed', _onStateChanged),
    // The iframe rebuilds its DOM on a full render; re-stamp the member attrs
    // or a live multi-selection goes invisible until the next click.
    eventBus.on('iframe:bridge-ready', _restampBridge)
  ];

  return () => {
    offStore();
    for (const off of offs) if (typeof off === 'function') off();
    _installed = false;
  };
}

/** Test hook — tears the singleton down so a suite can install a fresh one. */
export function _resetElementSelectionForTests() {
  _installed = false;
  _context = null;
  _lastPrunedHtml = null;
  elementSelection.clear({ source: 'test-reset' });
}
